import clsx from 'clsx'

import { useEffect, useState, useMemo } from 'preact/hooks'
import { formatMoney, globalState } from 'helpers/utils'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import useCartGlobal, { CartConfigKeys } from 'hooks/useCartGlobal'
import useURL from 'hooks/useUrl'

import { createPortal } from 'preact/compat'
import PreactImage from 'snippets/image/image'
import Icon from 'snippets/icon/icon'
import { usePromolines } from '@/components/hooks/usePromolines'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { hasB2BDiscount } from '@/components/custom-elements/b2b-price'

const actionKeys = {
  PLUS: 'plus',
  MINUS: 'minus',
  REMOVE: 'remove'
}

const onChangeActionValue = (key, quantity) => {
  return {
    [actionKeys.PLUS]: quantity + 1,
    [actionKeys.MINUS]: quantity - 1,
    [actionKeys.REMOVE]: 0
  }[key]
}

const OnChangeTrigger = ({ type, value, className, p }) => {
  return (
    <div class={`group shrink-0 relative cursor-pointer ${p}`}>
      <input
        type="checkbox"
        data-action={type}
        class=" absolute inset-tl size-full cursor-pointer appearance-none border-0 bg-transparent opacity-0 z-2"
        aria-label={value}
      />
      <div class={className}>{value}</div>
    </div>
  )
}

/**
 *
 * Handles line item quantity update.
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemQuantity = ({ item, handleSetCartConfig, simplified }) => {
  const plusIcon = <Icon id="plus" />
  const minusIcon = <Icon id="minus" />
  const trashIcon = <Icon id="trash" />
  const [error, setError] = useState(null)

  /** Checks if the current Line Item new quantity is above the available inventory  */
  const handleLineItemQuantityChange = (item, newQuantity) => {
    if (newQuantity > item.inventory_quantity) {
      setError('Maximum quantity reached')
      setTimeout(() => setError(null), 5000)
      throw new Error('Unavailable product stock quantity')
    }
    setError(null)
  }

  const onLineItemQuantityChange = async (e) => {
    try {
      e.preventDefault()
      /** Get Action Key */
      const action = e.target.dataset.action
      /** Set MiniCart state to isLoading true */
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      /** Get new line item quantity based off action */
      const newQuantity = onChangeActionValue(action, item.quantity)
      /** Throw an error is the new quantity is over the available stock */
      handleLineItemQuantityChange(item, newQuantity)

      // Check if this item has a customisationGroupId
      if (item.properties?._customisationGroupId) {
        const groupId = item.properties._customisationGroupId
        const relatedItems = window.Cart.value.items.filter(cartItem =>
          cartItem.properties?._customisationGroupId === groupId
        )

        if (newQuantity <= 0) {
          // Remove entire group when quantity goes to 0
          for (const relatedItem of relatedItems) {
            await window.Cart.updateLineItemQuantity(
              {
                id: relatedItem.variant_id,
                quantity: 0
              },
              { silent: false }
            )
          }
        } else {
          // Update all items in the group to the same quantity
          for (const relatedItem of relatedItems) {
            await window.Cart.updateLineItemQuantity(
              {
                id: relatedItem.variant_id,
                quantity: newQuantity
              },
              { silent: false }
            )
          }
        }
      } else {
        /** Update line item quantity for non-customisation items */
        await window.Cart.updateLineItemQuantity(
          {
            id: item.variant_id,
            quantity: newQuantity
          },
          { silent: false }
        )
      }

      /** Poll Shopify for new Cart item and set cart config state */
      await window.Cart.getCart()
      handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    } catch (error) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: error.message
      })
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    }
  }

  return (
    <>
      {simplified ? (
        <form onChange={onLineItemQuantityChange} class="absolute inset-br flex z-2">
          <OnChangeTrigger
            p="link-fade"
            type={actionKeys.REMOVE}
            value={'Remove'}
            className={'text-xs underline'}
          />
        </form>
      ) : (
        <form onChange={onLineItemQuantityChange} class="flex items-center justify-between gap-x-0.5 md:gap-x-1">
          <div class="shrink-0 flex border border-primary">
            <OnChangeTrigger
              p="btn btn-primary btn-transparent btn-square w-[36px] !border-0  disabled:!opacity-50 disabled:cursor-not-allowed"
              type={actionKeys.MINUS}
              value={minusIcon}
              className={'size-fit children:w-[12px] children:h-[2px]'}
            />
            <div class="grow flex items-center justify-center text-s w-2 text-center">{item.quantity}</div>
            <OnChangeTrigger
              p='btn btn-primary btn-transparent btn-square w-[36px] !border-0  disabled:!opacity-50 disabled:cursor-not-allowed'
              type={actionKeys.PLUS}
              value={plusIcon}
              className={'size-fit children:size-[12px]'}
            />
          </div>

          <OnChangeTrigger
            p="link-fade"
            type={actionKeys.REMOVE}
            value={trashIcon}
            className={'size-fit children:size-[18px]'}
          />
        </form>
      )}
      <p class={clsx('hidden w-full paragraph-xs text-system-error transition-all', error && '!flex')}>{error}</p>
    </>
  )
}

/**
 *
 *
 * Mini Cart Snippet,
 * Currently hardcoded to handle the free shipping discount range.
 *
 * @param {*} param0
 * @returns
 */
function MiniCartSnippet({ cartSubTotal, freeShippingGoal, cartItems }) {
  // Check if any cart item has heavy_item metafield set to true
  const hasHeavyItems = cartItems && cartItems.some(item => item.heavy_item === true)

  if (hasHeavyItems) {
    return (
      <div data-role="heavy_item_notice" class="flex flex-col gap-0.5 px-1 py-0.75 bg-system-warning-light transition-all">
        <div class="flex items-center justify-center gap-x-0.375 text-center">
          <p class="paragraph-xs font-bold text-system-warning">
            Please note that heavy items aren't eligible for free shipping
          </p>
        </div>
      </div>
    )
  }

  const handleFreeShippingValue = (amount) => {
    if (typeof amount === 'number' && amount > 0) {
      return amount
    }
    return parseInt(amount)
  }

  const freeShippingMinimum = handleFreeShippingValue(freeShippingGoal) ?? 10000
  const reachedGoal = freeShippingMinimum - cartSubTotal
  const message = reachedGoal <= 0 ? (
    <>
      <p class="paragraph-b whitespace-nowrap overflow-hidden text-ellipsis">You're receiving free shipping</p>
    </>
  ) : (
    <>
      <p class="paragraph-b whitespace-nowrap overflow-hidden text-ellipsis">You're <span class="font-bold">{formatMoney(reachedGoal)}</span> away from free shipping</p>
    </>
  )
  const progressBarPercentage = Math.round((cartSubTotal / freeShippingGoal) * 100)
  const progressBarPercentageWidth = progressBarPercentage > 100 ? '100' : progressBarPercentage
  return (
    <div data-role="free_shipping_monitor" class="flex flex-col gap-0.5 px-1 md:px-1.5 py-1 bg-neutral-light transition-all">
      <div class="flex items-center justify-center gap-x-0.375 text-center">
        {message}
      </div>
      <div class="relative w-full h-[4px] bg-neutral-medium">
        <div
          class={clsx(
            'absolute inset-tl w-shipping-bar-width h-full transition-all',
            reachedGoal <= 0 ? 'bg-system-success' : 'bg-brand-black'
          )}
          style={{ '--shipping-bar-width': progressBarPercentageWidth + '%' }}
        >
          <div class={clsx(
            'absolute top-1/2 right-0 flex items-center justify-center size-[14px] rounded-full -translate-y-1/2 translate-x-1/2 text-brand-white bg-system-success z-2 transition-all',
            reachedGoal <= 0 ? 'opacity-100' : 'opacity-0'
          )}>
            <div class="w-[7px] h-[6px] children:size-full">
              <Icon id={'tick'} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * B2B Line Price Component
 * Renders a b2b-price element directly in JSX
 */
const B2BLinePrice = ({ item }) => {
  return (
    <b2b-price class="group/price flex flex-col items-end text-right" data-variant-id={item.variant_id} data-quantity={item.quantity}>
      <span data-b2b-price class="paragraph-s group-data-[sale=true]/price:text-brand-red font-bold"></span>
      <span data-original-price class="hidden group-data-[sale=true]/price:block paragraph-s text-neutral-secondary line-through font-bold"></span>
    </b2b-price>
  )
}

/**
 *
 * Display line item price
 * Handles different price if discount is applied
 *
 * Props Cart Line Item
 *
 * @param {*} param0
 * @returns
 */
const ItemPrice = ({ item }) => {
  const SalePrice = ({ salePrice, originalPrice }) => {
    return (
      <div class="flex flex-col items-end text-right">
        <span class="paragraph-s text-brand-red font-bold">{formatMoney(salePrice)}</span>
        <span class="paragraph-s text-neutral-secondary line-through font-bold">{formatMoney(originalPrice)}</span>
      </div>
    )
  }

  // For B2B customers, calculate line total from unit price
  const isB2BCustomer = window.globals?.customer?.b2b

  if (isB2BCustomer && item.variant_id) {
    return <B2BLinePrice item={item} />
  }

  // Priority order for non-B2B: Discounts -> Compare At -> Regular
  if (item.discounts.length > 0 && item.original_line_price !== item.line_price) {
    return <SalePrice salePrice={item.line_price} originalPrice={item.original_line_price} />
  } else if (item.compare_at_price > item.original_price) {
    return <SalePrice salePrice={item.line_price} originalPrice={item.compare_at_price * item.quantity} />
  }

  return (
    <div class="flex flex-col items-end text-right">
      <span class="paragraph-s font-bold">{formatMoney(item.original_line_price)}</span>
    </div>
  )
}

const MiniCartItemDiscount = ({ item }) => {
  return (
    <>
      {item.discounts.length > 0 && (
        <ul class="list-none">
          {item.discounts.map((discount) => {
            return (
              <li class="flex gap-x-0.25 items-center paragraph-xs text-neutral-secondary list-none">
                <div class="size-[14px] children:size-full">
                  <Icon id={'tag'} />
                </div>
                {discount.title}
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}

/**
 *
 * Displays line item image
 *
 * @param {*} imageSrc
 * @param {*} id
 * @param {*} productPath
 * @returns
 */
const MiniCartItemImage = ({ imageSrc, id, productPath }) => {
  return (
    <a href={productPath} class="shrink-0 link-fade" aria-label="Item image">
      <PreactImage className="flex w-[64px] md:w-[150px] aspect-1/1 children:size-full children:object-cover" key={id} imageUrl={[imageSrc]} />
    </a>
  )
}

/**
 *
 * Display the line item
 * 1) Title
 * 2) Variant Details (size, colour)
 * 3) Discount details (if applicable)
 * 4) Line Item price
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemDetails = ({ item }) => {
  const properties = item.properties
  const hasProperties = properties && Object.keys(properties).length > 0

  const criteria = useMemo(() => ({
    tags: item.tags ?? [],
    collections: item.collections?.map(c => c.handle) ?? [],
    products: [item.handle]
  }), [item.tags, item.collections, item.handle])

  const allPromolines = usePromolines(criteria)

  const isB2BCustomer = window.globals?.customer?.b2b
  const b2bPricing = window.globals?.customer?.b2bPricing
  const productId = item.uid || item.id?.toString?.() || item.id
  const b2bData = isB2BCustomer && b2bPricing ? b2bPricing[productId] : null
  const hasQuickB2bCheck = isB2BCustomer && b2bData && b2bData.price
  const [b2bPriceCheck, setB2bPriceCheck] = useState(hasQuickB2bCheck)

  useEffect(() => {
    if (isB2BCustomer && item.variant_id) {
      hasB2BDiscount(item.variant_id).then(hasDiscount => {
        setB2bPriceCheck(hasDiscount)
      }).catch(() => {
        setB2bPriceCheck(false)
      })
    } else {
      setB2bPriceCheck(false)
    }
  }, [isB2BCustomer, item.variant_id])

  // Generic function to get first two items with exclusive logic
  const getObjects = (items) => {
    const exclusiveItem = items.find(item => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstPromoline, secondPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  return (
    <div class="grid grid-cols-full-min gap-x-0.5 md:gap-x-1">
      <div class="flex flex-col">
        <h3 class="paragraph-s font-bold uppercase">
          {item.product_title}
        </h3>
        <div class="flex flex-col gap-y-0.375 mt-0.375">
          <span class="paragraph-xs text-neutral-secondary">
            {item.options_with_values.map((option, index) => (
              <>
                {option.value}
                {index < item.options_with_values.length - 1 && <span class="text-neutral-medium">&nbsp; | &nbsp;</span>}
              </>
            ))}
          </span>
          {hasProperties && (
            <span class="paragraph-xs text-neutral-secondary">
              {properties && Object.keys(properties)
                .filter(property => properties[property] && !property.startsWith('_'))
                .map((property, index, filteredProperties) => (
                  <>
                    {property}: {properties[property]}
                    {index < filteredProperties.length - 1 && <br />}
                  </>
                ))
              }
            </span>
          )}
          <MiniCartItemDiscount item={item} />
        </div>
        {(b2bPriceCheck || firstPromoline || secondPromoline) && (
          <div class="flex flex-wrap gap-0.25 mt-0.5">
            {b2bPriceCheck && <Promoline promoline={{
              title: 'Wholesale Price',
              text_colour: '#9C0000',
              background_colour: '#FCEBEB'
            }} promolineLocation="Minicart" promolineSize="medium" />}
            {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="Minicart" promolineSize="medium" />}
            {!b2bPriceCheck && secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="Minicart" promolineSize="medium" />}
          </div>
        )}
      </div>
      <ItemPrice item={item} />
    </div>
  )
}

const MiniCartLineItem = ({ item, handleSetCartConfig, isLoading }) => {
  const loadingState = isLoading && 'opacity-50'
  const isCustomisationProduct = item.properties?._customProduct === 'true'

  return (
    <>
      {isCustomisationProduct ? (
        <div class={`flex flex-row gap-x-1 -mt-px pb-1.5 bg-brand-white border-b border-neutral-light last:border-b-0 transition-all z-2 ${loadingState}`}>
          <div class="size-[14px] children:size-full">
            <Icon id={'success'} />
          </div>
        <div class="relative flex-grow flex flex-col justify-between gap-y-1 size-full">
          <MiniCartItemDetails item={item} />
          <MiniCartItemQuantity item={item} handleSetCartConfig={handleSetCartConfig} simplified={true} />
        </div>
      </div>
      ) : (
        <div class={`flex flex-row md:items-center gap-x-0.5 md:gap-x-1 py-1.5 border-b border-neutral-light last:border-b-0 transition-all ${loadingState}`}>
          <MiniCartItemImage imageSrc={item.featured_image.url} id={item.id} productPath={item.url} />
          <div class="flex-grow flex flex-col justify-between gap-y-1 size-full md:py-0.5">
            <MiniCartItemDetails item={item} />
            <MiniCartItemQuantity item={item} handleSetCartConfig={handleSetCartConfig} />
          </div>
        </div>
      )}
    </>
  )
}

/**
 * Calculates total savings from compare_at_price discounts
 * @param {Array} cartItems - Array of cart items
 * @returns {number} - Total compare_at_price savings in cents
 */
const calculateCompareAtSavings = (cartItems) => {
  let compareAtSavings = 0

  for (const item of cartItems) {
    const linePricePerItem = item.line_price / item.quantity
    const compareAtPrice = item.compare_at_price

    if (compareAtPrice && compareAtPrice > linePricePerItem) {
      const savingsPerItem = compareAtPrice - linePricePerItem
      const itemSavings = savingsPerItem * item.quantity
      compareAtSavings += itemSavings
    }
  }

  return compareAtSavings
}

/**
 *
 * Displays cart total for Mini Cart.
 *
 * @param {*} param0
 * @returns
 */
const DisplayCartTotal = ({ total, totalCartDiscount, isLoading, cart, subtotalText, discountText, totalText }) => {
  const cartTotal = formatMoney(total)
  const compareAtSavings = calculateCompareAtSavings(cart.items)
  const totalDiscountAmount = totalCartDiscount + compareAtSavings
  const totalDiscount = formatMoney(totalDiscountAmount)
  const subtotal = formatMoney(total + totalDiscountAmount)

  return (
    <div class={`flex flex-col gap-y-1.25 transition-all ${isLoading && 'opacity-50'}`}>
      <div class="flex flex-col gap-y-0.75">
        <div class="flex justify-between gap-x-1">
          <p class="paragraph-s">{subtotalText || 'Subtotal'}</p>
          <p class="paragraph-s text-right">{subtotal}</p>
        </div>
        {!window.globals?.customer?.b2b && (
          <div class="flex justify-between gap-x-1">
            <p class="paragraph-s">{discountText || 'Discounts'}</p>
            <p class="paragraph-s text-right">-{totalDiscount}</p>
          </div>
        )}
      </div>
      <div class="flex justify-between gap-x-1">
        <p class="paragraph-b font-bold">{totalText || 'Total'}</p>
        <p class="paragraph-b font-bold text-right">{cartTotal}</p>
      </div>
    </div>
  )
}

/**
 *
 * Empty Cart state. Renders when the customers cart is empty
 *
 * @param {*} param0
 * @returns
 */
const EmptyCart = ({ isLoading, emptyDescription, emptyLink, emptyLinkText }) => {
  return (
    <>
      <div class={`grow flex flex-col gap-y-1 md:gap-y-1.5 items-center justify-center px-margin pt-6.25 pb-4.75 text-center transition-all ${isLoading ? 'opacity-0' : ''}`}>
        <p class={'h6 font-cnd font-bold uppercase'}>{emptyDescription}</p>
        {emptyLinkText &&
          <a href={emptyLink} className={'btn btn-primary btn-fill btn-m w-fit'} aria-label={emptyLinkText}>{emptyLinkText}</a>
        }
      </div>
    </>
  )
}

/**
 *
 * Cart Checkout button
 *
 * @param {*} param0
 * @returns
 */
const CheckoutButton = ({ checkoutButtonTitle }) => {
  return (
    <div>
      <a href="/checkout" class="btn btn-primary btn-fill btn-l w-full" aria-label={checkoutButtonTitle}>
        {checkoutButtonTitle}
      </a>
    </div>
  )
}

/**
 *
 * Displays the line items present in the the customers cart.
 *
 * @param {*} param0
 * @returns
 */
const CartLineItems = ({ cart, handleSetCartConfig, isLoading }) => {
  return (
    <div class="grow flex flex-col overflow-y-scroll no-scrollbar overscroll-none px-1 md:px-1.5" data-role="cart_items">
      {cart.items.map((item) => {
        return <MiniCartLineItem item={{ ...item }} handleSetCartConfig={handleSetCartConfig} isLoading={isLoading} />
      })}
    </div>
  )
}

/**
 *
 * Mini Cart
 *
 * @returns
 */
export function MiniCart() {
  const path = useURL()
  const { cartConfig, handleSetCartConfig } = useCartGlobal()
  const globalCart = globalState.cart
  const { isLoading, cart } = cartConfig
  const freeShippingMinimum = globalCart?.freeShippingMinimum

  const { checkoutButtonTitle, emptyDescription, emptyLink, emptyLinkText, subtotalText, discountText, totalText } = globalCart

  useEffect(() => {
    const altPaymentButtons = document.querySelector('div.additional-checkout-buttons--vertical')
    if (!altPaymentButtons) return
    if (cart.items.length > 0) {
      altPaymentButtons.classList.remove('hidden')
    } else if (cart.items.length <= 0) {
      altPaymentButtons.classList.add('hidden')
    }
  }, [cartConfig])

  useEffect(() => {
    if (path.includes('timestamp=')) {
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      window.Cart.getCart().then(() => {
        handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
        handleSetCartConfig(CartConfigKeys.isLoading, false)
      })
    }
  }, [path])

  return (
    <>
      {cart?.items?.length ? (
        <>
          <MiniCartPortal targetId="minicart-shipping">
            <MiniCartSnippet cartSubTotal={cart.items_subtotal_price} freeShippingGoal={freeShippingMinimum} cartItems={cart.items} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-items">
            <CartLineItems cart={cart} isLoading={isLoading} handleSetCartConfig={handleSetCartConfig} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-summary">
            <div className="flex flex-col gap-y-1.25">
              <DisplayCartTotal
                totalCartDiscount={cart.total_discount}
                total={cart.total_price}
                isLoading={isLoading}
                cart={cart}
                subtotalText={subtotalText}
                discountText={discountText}
                totalText={totalText}
              />
              <CheckoutButton checkoutButtonTitle={checkoutButtonTitle} />
            </div>
          </MiniCartPortal>
        </>
      ) : (
        <MiniCartPortal targetId="minicart-items">
          <EmptyCart cartConfig={cartConfig} globalCart={globalCart} isLoading={isLoading} emptyDescription={emptyDescription} emptyLink={emptyLink} emptyLinkText={emptyLinkText} />
        </MiniCartPortal>
      )}
    </>
  )
}

const MiniCartPortal = ({ children, targetId }) => {
  const target = document.getElementById(targetId)
  return target ? createPortal(children, target) : null
}
